import { Card, Tag, ProgressBar, Title, Text } from '@nimbus-ds/components'
// Nimbus icon substitutions: MousePointer→StepsIcon (no cursor/click icon), Eye→EyeIcon,
// ShoppingCart→ShoppingCartIcon, CreditCard→CreditCardIcon, TrendingUp→ArrowupIcon,
// TrendingDown→ArrowDownIcon.
import { StepsIcon, EyeIcon, ShoppingCartIcon, CreditCardIcon, ArrowupIcon, ArrowDownIcon } from '@nimbus-ds/icons'

interface EventMetrics {
  event_name: string
  event_type?: string
  control: {
    count: number
    unique_sessions: number
    rate?: number
  }
  treatment: {
    count: number
    unique_sessions: number
    rate?: number
  }
  is_significant?: boolean
  p_value?: number
  lift_percentage?: number
  confidence_level?: number
}

interface CustomEventsAnalyticsProps {
  events: EventMetrics[]
  totalSessions: {
    control: number
    treatment: number
  }
}

const eventIcons: Record<string, any> = {
  click: StepsIcon,
  pageview: EyeIcon,
  add_to_cart: ShoppingCartIcon,
  purchase: CreditCardIcon,
}

const eventColors: Record<string, string> = {
  click: '#3b82f6',
  pageview: '#8b5cf6',
  add_to_cart: '#f59e0b',
  purchase: '#10b981',
}

export function CustomEventsAnalytics({ events, totalSessions }: CustomEventsAnalyticsProps) {
  if (!events || events.length === 0) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Análisis de Eventos Personalizados</Title>
          <Text>
            Eventos personalizados rastreados durante el experimento
          </Text>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-12">
            <StepsIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <Text>No hay eventos personalizados rastreados aún</Text>
            <Text fontSize="caption">
              Los eventos capturados desde el Editor Visual aparecerán aquí
            </Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const calculateLift = (treatment: number, control: number) => {
    if (control === 0) return 0
    return ((treatment - control) / control) * 100
  }

  // One row per event: sessions-with-event rate per arm + observed lift. The bar chart that used to sit
  // above duplicated these counts; the "Insights Clave" block below them (most frequent / biggest lift /
  // total) is degenerate with one or two events and said nothing a consultant could act on.
  return (
    <div className="space-y-6">
      <Card>
        <Card.Header>
          <Title as="h4">Eventos personalizados</Title>
          <Text fontSize="caption">
            % de sesiones de cada variante con el evento. Lift observado, sin veredicto (los eventos no están en el umbral de decisión).
          </Text>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            {events.map((event, index) => {
              const Icon = eventIcons[event.event_type || 'click'] || StepsIcon
              const color = eventColors[event.event_type || 'click'] || '#3b82f6'

              const controlRate = event.control.rate || (event.control.unique_sessions / totalSessions.control * 100)
              const treatmentRate = event.treatment.rate || (event.treatment.unique_sessions / totalSessions.treatment * 100)
              const rateLift = calculateLift(treatmentRate, controlRate)

              const isPositiveLift = rateLift > 0

              return (
                <div
                  key={index}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                >
                  {/* Event header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div>
                        <Title as="h4">{event.event_name}</Title>
                        {event.event_type && (
                          <div className="mt-1">
                            <Tag appearance="neutral">
                              {event.event_type}
                            </Tag>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Observed lift, neutral tone — descriptive, not a verdict. */}
                      <Tag appearance="neutral">
                        {isPositiveLift ? (
                          <ArrowupIcon className="w-3 h-3" />
                        ) : (
                          <ArrowDownIcon className="w-3 h-3" />
                        )}
                        {rateLift > 0 ? '+' : ''}{rateLift.toFixed(1)}% lift
                      </Tag>
                    </div>
                  </div>

                  {/* Metrics comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Control */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Text>Control</Text>
                        <Text fontWeight="bold" color="neutral-textHigh" as="span">
                          {event.control.count.toLocaleString()} eventos
                        </Text>
                      </div>
                      <ProgressBar value={controlRate} appearance="primary" />
                      <div className="flex items-center justify-between">
                        <Text fontSize="caption" as="span">{event.control.unique_sessions.toLocaleString()} sesiones</Text>
                        <Text fontSize="caption" as="span">{controlRate.toFixed(2)}% tasa</Text>
                      </div>
                    </div>

                    {/* Treatment */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Text>Tratamiento</Text>
                        <Text fontWeight="bold" color="neutral-textHigh" as="span">
                          {event.treatment.count.toLocaleString()} eventos
                        </Text>
                      </div>
                      <ProgressBar value={treatmentRate} appearance="success" />
                      <div className="flex items-center justify-between">
                        <Text fontSize="caption" as="span">{event.treatment.unique_sessions.toLocaleString()} sesiones</Text>
                        <Text fontSize="caption" as="span">{treatmentRate.toFixed(2)}% tasa</Text>
                      </div>
                    </div>
                  </div>

                </div>
              )
            })}
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
